import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getDecrossPublishStatus, publishAppToDecross } from "@/services/decross";
import type { DecrossPublishStatus } from "@/services/decross";

interface DecrossPublishControlProps {
  /** 已保存的应用 id；null（未保存）时按钮禁用、不显示徽标状态。 */
  appId: string | null;
  /** 应用名（仅用于调试提示）。 */
  appName: string;
}

interface BadgeView {
  text: string;
  variant: "outline" | "secondary" | "destructive" | "default";
  className?: string;
}

/** 状态徽标：未发布 / 发布中 / 审核中 / 已发布 / 已拒绝（+原因）/ 发布失败。 */
function badgeFor(status: DecrossPublishStatus | null): BadgeView {
  if (!status || status.status === "not_submitted") {
    return { text: "未发布", variant: "outline" };
  }
  if (status.status === "pending") {
    return { text: "发布中…", variant: "secondary" };
  }
  if (status.status === "error") {
    return { text: "发布失败", variant: "destructive" };
  }
  // submitted：以 decross 侧审核状态细分
  switch (status.decross_status) {
    case "published":
      return { text: "已发布", variant: "secondary", className: "bg-green-600 hover:bg-green-600" };
    case "rejected":
      return { text: "已拒绝", variant: "destructive" };
    case "draft":
    case "pending_review":
      return { text: "审核中", variant: "outline" };
    default:
      return { text: "审核中", variant: "outline" };
  }
}

/** 发布到坊市的控制器：状态徽标 + 发布按钮 + 失败提示（替换 toast，样式跟随状态栏）。 */
export function DecrossPublishControl({ appId, appName }: DecrossPublishControlProps) {
  const [status, setStatus] = React.useState<DecrossPublishStatus | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // 切换应用（加载/新建）时重新拉取发布状态；后端端点未就绪时静默按未发布处理
  React.useEffect(() => {
    if (!appId) {
      setStatus(null);
      setError(null);
      return;
    }
    let cancelled = false;
    getDecrossPublishStatus(appId)
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        if (!cancelled) setStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [appId]);

  const refreshStatus = async (appIdToPoll: string) => {
    const s = await getDecrossPublishStatus(appIdToPoll);
    setStatus(s);
    return s;
  };

  const handlePublish = async () => {
    if (!appId) return;
    setBusy(true);
    setError(null);
    try {
      await publishAppToDecross(appId);
      // 发布为异步任务：轮询状态直到脱离 pending（打包+提交一般在几秒内完成）
      for (let attempt = 0; attempt < 10; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const s = await refreshStatus(appId);
        if (s.status !== "pending") break;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      if (appId) {
        refreshStatus(appId).catch(() => undefined);
      }
    } finally {
      setBusy(false);
    }
  };

  const badge = badgeFor(status);
  const rejectedReason =
    status?.decross_status === "rejected" ? status.review_reason : null;

  return (
    <div className="flex items-center gap-2" data-testid="decross-publish-control">
      <Badge
        variant={badge.variant}
        className={badge.className}
        data-testid="decross-publish-badge"
        data-status={status?.status ?? "not_submitted"}
        data-decross-status={status?.decross_status ?? null}
        title={rejectedReason ? `驳回原因：${rejectedReason}` : undefined}
      >
        {badge.text}
        {rejectedReason && <span className="ml-1 max-w-[180px] truncate">（{rejectedReason}）</span>}
      </Badge>
      <Button
        variant="outline"
        size="sm"
        disabled={!appId || busy}
        onClick={() => void handlePublish()}
        data-testid="decross-publish-button"
        className="h-7 px-2.5 text-xs"
        title={appId ? `发布「${appName}」到坊市（decross 审核后上架）` : "请先保存应用"}
      >
        {busy ? "发布中…" : "发布到坊市"}
      </Button>
      {error && (
        <span className="max-w-[240px] truncate text-xs text-destructive" data-testid="decross-publish-error" title={error}>
          {error}
        </span>
      )}
    </div>
  );
}
